/* Noom LP bonus UI. The engine owns free-line reconciliation; paid actions stay in the existing cart runtime. */
(() => {
  'use strict';
  const bonus = window.HMLpBonus;
  if (!bonus || window.HMLpBonusUI) return;
  const config = JSON.parse(document.getElementById('hm-lp-bonus-config').textContent);
  const messages = config.messages || {};
  let failed = false;
  let dirty = false;
  let checkoutPass = null;
  let verifiedCart = null;
  let queuedCheckout = null;
  let verifyingCheckout = false;

  function bundledCart(response) {
    for (const [name, selector] of [
      ['hm-checkout-state', 'script[data-hm-checkout-state]'],
      ['hm-cart-drawer', '#CartDrawer script[data-hm-cart-state]'],
    ]) {
      const html = response?.sections?.[name];
      if (typeof html !== 'string') continue;
      try {
        const document = new DOMParser().parseFromString(html, 'text/html');
        const receipt = document.querySelector(selector);
        const cart = JSON.parse(receipt?.textContent || 'null');
        if (cart && Array.isArray(cart.items) && typeof cart.currency === 'string') {
          return { ...cart, sections: response.sections };
        }
      } catch (_) { /* Missing receipt requires one fresh cart read. */ }
    }
    return null;
  }

  // Compare checkout-relevant server state, not the cart token or presentation metadata.
  // Every financial field, line identity and fulfillment field stays in the comparison.
  function cartSnapshot(cart) {
    if (!cart || !Array.isArray(cart.items) || typeof cart.currency !== 'string') return null;
    const fields = (object, names) => Object.fromEntries(names.map(name => [name, object?.[name]]));
    const state = {
      ...fields(cart, ['currency', 'item_count', 'items_subtotal_price', 'original_total_price', 'total_price',
        'total_discount', 'total_weight', 'requires_shipping', 'note', 'attributes', 'discount_codes',
        'cart_level_discount_applications']),
      items: cart.items.map(item => fields(item, ['key', 'id', 'product_id', 'variant_id', 'quantity',
        'price', 'original_price', 'discounted_price', 'final_price', 'line_price', 'original_line_price',
        'final_line_price', 'total_discount', 'line_level_total_discount', 'discounts', 'discount_allocations',
        'line_level_discount_allocations', 'properties', 'selling_plan', 'selling_plan_allocation',
        'requires_shipping', 'taxable', 'gift_card', 'grams', 'title', 'product_title', 'variant_title'])),
    };
    return JSON.stringify(state, (_, value) => value && typeof value === 'object' && !Array.isArray(value)
      ? Object.fromEntries(Object.keys(value).sort().map(key => [key, value[key]])) : value);
  }

  function needsFreshView(drawer) {
    return dirty || failed || document.hidden || Boolean(drawer && (drawer.backgrounded || drawer.crossTabDirty ||
      drawer.needsSync || drawer.unconfirmed ||
      drawer.pendingAdd || drawer.reading || drawer.retrying || drawer.classList.contains('is-adding') ||
      drawer.classList.contains('has-error')));
  }

  const ui = window.HMLpBonusUI = {
    inputIdentity(input) {
      let properties = {};
      try { properties = JSON.parse(input.dataset.hmLineProperties || '{}') || {}; } catch (_) { /* older fragment */ }
      // Liquid renders an empty properties hash as [] on some product lines.
      if (Array.isArray(properties)) {
        if (properties.length) return null;
        properties = {};
      }
      return bonus.identityOfItem({ variant_id: input.dataset.quantityVariantId, properties,
        selling_plan_allocation: { selling_plan: { id: input.dataset.hmSellingPlan || '' } } });
    },
    itemIdentity(item) { return bonus.identityOfItem(item); },
    needsRecovery() { return failed || dirty; },
    markDirty() { dirty = true; verifiedCart = null; queuedCheckout = null; },
    cancelCheckout() { queuedCheckout = null; },
    prepareAdd(form) {
      const drawer = document.querySelector('cart-drawer');
      // pendingAdd/is-adding belong to THIS captured submit, so they are not stale-state guards here.
      if (dirty || failed || document.hidden || !drawer || drawer.backgrounded || drawer.crossTabDirty ||
          drawer.needsSync || drawer.unconfirmed || drawer.reading || drawer.retrying) return null;
      return bonus.prepareAdd?.(form) || null;
    },
    async settle(primary, sections, expectedForm, options = {}) {
      // An accepted paid write is final, even if its free bonus fails afterwards.
      try {
        const complete = Array.isArray(primary.items) && primary.currency ? primary : bundledCart(primary);
        const result = await bonus.reconcile({ cart: complete || undefined,
          sections, sectionsUrl: window.location.pathname + window.location.search,
          ...(options.deadlineAt == null ? {} : { deadlineAt: options.deadlineAt }) });
        if (expectedForm && !bonus.confirmsAdd(expectedForm, result)) {
          throw new Error('Bundled cart did not confirm the requested paid item');
        }
        failed = false;
        dirty = false;
        verifiedCart = cartSnapshot(result);
        return result;
      } catch (error) {
        failed = true;
        dirty = true;
        verifiedCart = null;
        queuedCheckout = null;
        console.warn('Noom bonus requires a fresh cart read:', error.code || 'BONUS_FAILED');
        if (expectedForm) throw Object.assign(error, { unconfirmed: true });
        if (options.strict) throw error;
        return primary;
      }
    },
    async recover(drawer) {
      const token = bonus.begin();
      if (!token) throw new Error('Bonus operation in progress');
      ui.render();
      try {
        const result = await bonus.reconcile({ sections: drawer.getSectionsToRender().map(s => s.id),
          sectionsUrl: window.location.pathname + window.location.search });
        if (result.bonusSectionsMissing) throw new Error('Bonus sections missing');
        failed = false;
        dirty = false;
        verifiedCart = cartSnapshot(result);
        return result.sections;
      } catch (error) {
        failed = true;
        dirty = true;
        verifiedCart = null;
        throw error;
      } finally {
        bonus.end(token);
        ui.render();
      }
    },
    render() {
      const busy = bonus.active || (typeof CartItems !== 'undefined' && Boolean(CartItems.activeUpdate || CartItems.draining));
      document.querySelectorAll('.cart-drawer__footer, #main-cart-footer .js-contents').forEach(footer => {
        let note = footer.querySelector('[data-hm-bonus-status]');
        if (!note && (bonus.active || failed)) {
          note = document.createElement('div');
          note.className = 'hm-lp-bonus-status';
          note.dataset.hmBonusStatus = '';
          note.setAttribute('role', 'status');
          const text = document.createElement('p');
          const retry = document.createElement('button');
          retry.type = 'button';
          retry.dataset.hmBonusRetry = '';
          retry.textContent = messages.retry || 'Odśwież koszyk';
          note.append(text, retry);
          footer.prepend(note);
        }
        if (!note) return;
        note.hidden = !bonus.active && !failed;
        note.querySelector('p').textContent = busy ? messages.pending || 'Sprawdzamy prezent…' :
          messages.error || 'Nie udało się potwierdzić bonu. Odśwież koszyk przed przejściem do kasy.';
        note.querySelector('button').hidden = busy || !failed;
      });
      document.querySelectorAll('[name="checkout"]').forEach(button => {
        if (busy || failed) button.setAttribute('aria-busy', busy ? 'true' : 'false');
        else button.removeAttribute('aria-busy');
      });
      if (!busy && queuedCheckout) {
        const intent = queuedCheckout;
        queuedCheckout = null;
        if (!failed && !dirty) Promise.resolve().then(() => {
          if (failed || dirty) return;
          const form = intent.formId ? document.getElementById(intent.formId) : null;
          const submitter = intent.submitterId ? document.getElementById(intent.submitterId) : form?.querySelector('[name="checkout"]');
          if (intent.href || (form && submitter)) requestCheckout(form, submitter, intent.href);
        });
      }
    },
    updateBars() {
      document.querySelectorAll('[data-hm-bonus-bar]').forEach(bar => {
        const form = bar.closest('form');
        const marker = form?.querySelector('[data-hm-bonus-source]');
        const variantId = String(form?.querySelector('[name="id"]')?.value || '');
        if (marker) marker.disabled = variantId !== marker.dataset.hmBonusVariant;
        const qualifiesVariant = config.eligibility === 'product'
          ? Array.isArray(config.variantIds) && config.variantIds.some(id => String(id) === variantId)
          : Boolean(marker) && !marker.disabled;
        const quantity = Number(form?.querySelector('[name="quantity"]')?.value || 1);
        let value = quantity === 1 ? 50 : quantity === 2 ? 120 : 0;
        if (quantity >= 3 && config.quantityPolicy === 'cap120') value = 120;
        if (quantity >= 3 && config.quantityPolicy === 'pairs') value = Math.floor(quantity / 2) * 120 + quantity % 2 * 50;
        bar.hidden = value === 0 || !qualifiesVariant;
        bar.querySelectorAll('[data-hm-bonus-value]').forEach(node => { node.textContent = String(value); });
        const title = bar.querySelector('[data-hm-bonus-title]');
        if (title) title.textContent = value === 50 ? messages.barOne : value === 120 ? messages.barTwo : 'Bony ' + value + ' zł gratis';
      });
    }
  };

  // The normal quantity bundles update a hidden input synchronously on click.
  // Run after that handler, without a mutation observer or polling.
  document.addEventListener('change', () => ui.updateBars());
  document.addEventListener('elora:selection', () => ui.updateBars());
  document.addEventListener('click', event => {
    if (!event.target.closest('[data-hm-bonus-retry]')) return;
    event.preventDefault();
    const drawer = document.querySelector('cart-drawer');
    if (drawer && !bonus.active) drawer.retryRead();
  });
  document.addEventListener('shopify:section:load', () => { ui.updateBars(); ui.render(); });

  async function verifyCheckout(form, submitter, href) {
    const token = bonus.begin();
    if (!token) { ui.render(); return; }
    verifyingCheckout = true;
    ui.render();
    try {
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender().map(s => s.id) || [];
      const sectionsUrl = window.location.pathname + window.location.search;
      const candidate = Boolean(verifiedCart && !needsFreshView(drawer));
      // The engine still reads and validates the real cart and gift. Only HTML rendering is deferred.
      let result = await bonus.reconcile({ sections: candidate ? [] : sections, sectionsUrl });
      // An absent marker (older engine) also takes the full path. A repaired gift
      // can make the final cart equal the old snapshot, yet the drawer still needs rendering.
      const unchanged = candidate && result.bonusAdjusted === false && !needsFreshView(drawer) &&
        cartSnapshot(result) === verifiedCart;
      if (candidate && !unchanged) {
        // If another tab or an uncertain write appeared during verification, read afresh.
        // Otherwise the engine's confirmed result can supply the full sections path.
        result = await bonus.reconcile({ ...(!needsFreshView(drawer) ? { cart: result } : {}),
          sections, sectionsUrl });
      }
      if (result.bonusSectionsMissing) throw new Error('Checkout cart sections missing');
      failed = false;
      dirty = false;
      verifiedCart = cartSnapshot(result);
      if (href) { window.location.assign(href); return; }
      const formId = form.id;
      const submitterId = submitter?.id;
      if (!unchanged && drawer && result.sections) drawer.renderContents(result);
      const currentForm = formId ? document.getElementById(formId) : form;
      const currentButton = submitterId ? document.getElementById(submitterId) : currentForm?.querySelector('[name="checkout"]');
      if (!currentForm || !currentButton) throw new Error('Checkout form no longer available');
      checkoutPass = currentForm;
      try { currentForm.requestSubmit(currentButton); }
      finally { checkoutPass = null; }
    } catch (error) {
      failed = true;
      dirty = true;
      verifiedCart = null;
      ui.render();
    } finally {
      bonus.end(token);
      verifyingCheckout = false;
      ui.render();
    }
  }

  function requestCheckout(form, submitter, href) {
    if (verifyingCheckout) return;
    if (typeof CartItems !== 'undefined') CartItems.flushPendingChanges?.();
    if (bonus.active || (typeof CartItems !== 'undefined' && (CartItems.activeUpdate || CartItems.draining))) {
      // Keep one deliberate click across the re-render caused by the pending add/change.
      queuedCheckout = { formId: form?.id, submitterId: submitter?.id, href };
      return;
    }
    verifyCheckout(form, submitter, href);
  }

  document.addEventListener('submit', event => {
    if (event.target === checkoutPass) return;
    const form = event.target;
    const checkout = event.submitter?.name === 'checkout' || /\/checkout\/?$/.test(new URL(form.action, location.href).pathname);
    if (!checkout || (!bonus.relevant && !dirty && !failed && !bonus.active)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    requestCheckout(form, event.submitter);
  }, true);
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || !/\/checkout\/?$/.test(new URL(link.href, location.href).pathname)) return;
    if (!bonus.relevant && !dirty && !failed && !bonus.active) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    requestCheckout(null, null, link.href);
  }, true);
  window.addEventListener('pageshow', event => {
    if (event.persisted && bonus.relevant) { dirty = true; verifiedCart = null; }
  });
  ui.updateBars();
})();
